/**
 * Received Interests Page
 * Shows all interests received on leads owned by the current user
 * Module 5B - I am Interested Feature
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Avatar } from 'primereact/avatar';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { Badge } from 'primereact/badge';
import { Tooltip } from 'primereact/tooltip';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { SelectButton } from 'primereact/selectbutton';
import { Paginator } from 'primereact/paginator';

import {
    marketplaceService,
    INTEREST_STATUS,
    PRODUCT_TYPE_DETAILED_OPTIONS,
    getInterestStatusConfig,
    getLoanProductConfig,
    getGradeConfig,
    formatTimeAgo,
    formatLoanAmount
} from '../../services/marketplaceService';

import { ReviewInterestDialog } from '../../components/marketplace';
import NavigationTabMenu from '../../components/common/NavigationTabMenu';

// Sell Leads section tabs
const SELL_LEADS_TABS = [
    { label: 'My Listings', icon: 'pi pi-list', route: '/app/marketplace/my-listings' },
    { label: 'Buyer Enquiries', icon: 'pi pi-inbox', route: '/app/marketplace/received-interests' },
    { label: 'Post My Lead', icon: 'pi pi-plus', route: '/app/marketplace/post' }
];

// Helper to get lead name with fallbacks
const getLeadName = (interest) => {
    const lead = interest?.lead || {};
    if (lead.borrower_name) return lead.borrower_name;
    if (lead.masked_name) return lead.masked_name;
    if (lead.full_name) return lead.full_name;
    if (lead.name) return lead.name;
    if (lead.first_name || lead.last_name) {
        return `${lead.first_name || ''} ${lead.last_name || ''}`.trim();
    }
    const snapshot = interest?.lead_snapshot || {};
    if (snapshot.borrower_name) return snapshot.borrower_name;
    if (snapshot.full_name) return snapshot.full_name;
    return 'Lead';
};

// Outline style for Tags - border only, no solid fill
const outlineTagStyle = (severity) => {
    const colors = {
        warning: { background: 'transparent', border: '1.5px solid #EAB308', color: '#EAB308' },
        info: { background: 'transparent', border: '1.5px solid #3B82F6', color: '#3B82F6' },
        success: { background: 'transparent', border: '1.5px solid #22C55E', color: '#22C55E' },
        danger: { background: 'transparent', border: '1.5px solid #EF4444', color: '#EF4444' },
        secondary: { background: 'transparent', border: '1.5px solid #94A3B8', color: '#64748B' },
    };
    return colors[severity] || colors.secondary;
};

// Get product type — backend sends lead.product_type from formatInterestResponse
// Fallback to 'personal_loan' like ListingCard.js does
const getProductType = (lead) => {
    return lead?.product_type || 'personal_loan';
};

const LOAN_TYPE_OPTIONS = PRODUCT_TYPE_DETAILED_OPTIONS;

const ReceivedInterests = () => {
    const navigate = useNavigate();
    const toast = useRef(null);
    const searchTimeoutRef = useRef(null);

    // Data state
    const [interests, setInterests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pendingCount, setPendingCount] = useState(0);
    const [summary, setSummary] = useState({});

    // Filter state
    const [statusFilter, setStatusFilter] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [loanTypeFilter, setLoanTypeFilter] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    // Pagination
    const [page, setPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [first, setFirst] = useState(0);
    const rowsPerPage = 12;

    // Dialog state
    const [selectedInterest, setSelectedInterest] = useState(null);
    const [showReviewDialog, setShowReviewDialog] = useState(false);

    // View mode
    const [viewMode, setViewMode] = useState('grid');
    const viewOptions = [
        { icon: 'pi pi-th-large', value: 'grid' },
        { icon: 'pi pi-list', value: 'list' }
    ];

    // Debounce search query
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 500);
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchQuery]);

    // Load interests
    const loadInterests = async () => {
        setLoading(true);
        try {
            const params = {
                status: statusFilter || undefined,
                page,
                per_page: rowsPerPage
            };
            if (debouncedSearchQuery) {
                params.search = debouncedSearchQuery;
            }
            if (loanTypeFilter) {
                params.loan_type = loanTypeFilter;
            }
            if (startDate) {
                params.date_from = startDate.toISOString().split('T')[0];
            }
            if (endDate) {
                params.date_to = endDate.toISOString().split('T')[0];
            }
            const response = await marketplaceService.getReceivedInterests(params);

            if (response.success) {
                setInterests(response.data.interests || []);
                setTotalRecords(response.data.pagination?.total || 0);
                setPendingCount(response.data.pending_action_count || 0);
                setSummary(response.data.summary || {});
            }
        } catch (error) {
            console.error('Failed to load interests:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load received interests'
            });
        } finally {
            setLoading(false);
        }
    };

    // Reset pagination when filters change
    useEffect(() => {
        setFirst(0);
        setPage(1);
    }, [loanTypeFilter, startDate, endDate]);

    useEffect(() => {
        loadInterests();
    }, [statusFilter, page, debouncedSearchQuery, loanTypeFilter, startDate, endDate]);

    // Handle page change
    const onPageChange = (e) => {
        setFirst(e.first);
        setPage(Math.floor(e.first / rowsPerPage) + 1);
    };

    // Handle review interest
    const handleReviewInterest = (interest) => {
        setSelectedInterest(interest);
        setShowReviewDialog(true);
    };

    // Handle view lead
    const handleViewLead = (leadId) => {
        navigate(`/app/marketplace/listing/${leadId}`);
    };

    // Helper: get border color class by status
    const getBorderClass = (status) => {
        if (status === 'pending') return 'border-blue-500';
        if (status === 'countered') return 'border-orange-500';
        if (status === 'accepted') return 'border-green-500';
        return 'border-red-400';
    };

    // Helper: get avatar bg by status
    const getAvatarBg = (status) => {
        if (status === 'accepted') return 'bg-green-500';
        if (status === 'countered') return 'bg-orange-500';
        if (status === 'pending') return 'bg-blue-500';
        return 'bg-red-400';
    };

    // ─── Grid Card Component (Seller Perspective) ───
    const InterestCard = ({ interest }) => {
        const statusConfig = getInterestStatusConfig(interest.status);
        const isPending = interest.status === 'pending';
        const isCountered = interest.status === 'countered';
        const isAccepted = interest.status === 'accepted';
        const isRejected = interest.status === 'rejected';
        const productConfig = getLoanProductConfig(getProductType(interest.lead));
        const gradeConfig = getGradeConfig(interest.lead?.quality_grade);

        return (
            <div
                className={`surface-card border-round border-1 surface-border p-2 hover:shadow-2 transition-all transition-duration-200 h-full flex flex-column border-left-3 ${getBorderClass(interest.status)}`}
            >
                {/* Header: Product Tag + Grade Badge */}
                <div className="flex align-items-center justify-content-between mb-1">
                    <Tag
                        value={productConfig.label}
                        icon={`pi ${productConfig.icon}`}
                        style={{ fontSize: '0.55rem', padding: '0.1rem 0.35rem', backgroundColor: 'var(--primary-100)', color: 'var(--primary-700)', border: 'none' }}
                    />
                    <div
                        className="flex align-items-center justify-content-center border-round flex-shrink-0"
                        style={{
                            width: '22px', height: '22px',
                            backgroundColor: gradeConfig.bgColor,
                            color: gradeConfig.color,
                            fontWeight: 700, fontSize: '0.65rem'
                        }}
                    >
                        {interest.lead?.quality_grade || '-'}
                    </div>
                </div>

                {/* Borrower Name */}
                <div className="font-semibold text-900 mb-1 white-space-nowrap overflow-hidden text-overflow-ellipsis"
                     style={{ fontSize: '0.8rem' }}>
                    {getLeadName(interest)}
                </div>

                {/* Loan Amount */}
                <div className="text-green-600 font-bold mb-1" style={{ fontSize: '0.85rem' }}>
                    {formatLoanAmount(interest.lead?.loan_amount)}
                </div>

                {/* Location */}
                {(interest.lead?.city || interest.lead?.state) && (
                    <div className="flex align-items-center gap-1 text-500 mb-1" style={{ fontSize: '0.65rem' }}>
                        <i className="pi pi-map-marker" style={{ fontSize: '0.55rem' }} />
                        <span className="text-overflow-ellipsis white-space-nowrap overflow-hidden">
                            {[interest.lead?.city, interest.lead?.state].filter(Boolean).join(', ')}
                        </span>
                    </div>
                )}

                {/* Divider */}
                <div className="border-top-1 surface-border my-1" />

                {/* Agent Info */}
                <div className="flex align-items-center gap-1 mb-1">
                    <Avatar
                        image={interest.interested_agent?.avatar}
                        label={interest.interested_agent?.name?.charAt(0) || 'A'}
                        size="normal"
                        shape="circle"
                        className={`flex-shrink-0 ${getAvatarBg(interest.status)} text-white`}
                        style={{ width: '1.4rem', height: '1.4rem', fontSize: '0.6rem' }}
                    />
                    <span className="text-700 font-medium white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.7rem' }}>
                        {interest.interested_agent?.name || 'Agent'}
                    </span>
                </div>

                {/* Negotiation Section */}
                <div className="flex flex-column gap-1 mb-1">
                    <div className="flex align-items-center justify-content-between">
                        <span className="text-500" style={{ fontSize: '0.6rem' }}>Their Offer</span>
                        <div className="bg-blue-50 border-round px-2" style={{ padding: '1px 0' }}>
                            <span className="text-blue-600 font-bold" style={{ fontSize: '0.75rem' }}>
                                {parseFloat(Number(interest.proposed_percentage).toFixed(2))}%
                            </span>
                        </div>
                    </div>
                    {isCountered && interest.counter_percentage && (
                        <div className="flex align-items-center justify-content-between">
                            <span className="text-500" style={{ fontSize: '0.6rem' }}>Your Counter</span>
                            <div className="bg-orange-50 border-round px-2" style={{ padding: '1px 0' }}>
                                <span className="text-orange-600 font-bold" style={{ fontSize: '0.75rem' }}>
                                    {parseFloat(Number(interest.counter_percentage).toFixed(2))}%
                                </span>
                            </div>
                        </div>
                    )}
                    {isAccepted && interest.final_percentage && (
                        <div className="flex align-items-center justify-content-between">
                            <span className="text-500" style={{ fontSize: '0.6rem' }}>Agreed</span>
                            <div className="bg-green-50 border-round px-2" style={{ padding: '1px 0' }}>
                                <span className="text-green-600 font-bold" style={{ fontSize: '0.75rem' }}>
                                    {parseFloat(Number(interest.final_percentage).toFixed(2))}%
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Interest message */}
                {interest.interest_message && (
                    <div className="text-500 mb-1 font-italic white-space-nowrap overflow-hidden text-overflow-ellipsis"
                         style={{ fontSize: '0.6rem' }}>
                        "{interest.interest_message}"
                    </div>
                )}

                {/* Status + Time */}
                <div className="flex align-items-center justify-content-between mt-auto mb-1">
                    <Tag
                        value={statusConfig.label}
                        style={{ ...outlineTagStyle(statusConfig.severity), fontSize: '0.55rem', padding: '0.1rem 0.3rem' }}
                    />
                    <span className="text-400 flex align-items-center gap-1" style={{ fontSize: '0.6rem' }}>
                        <i className="pi pi-clock" style={{ fontSize: '0.5rem' }} />
                        {formatTimeAgo(interest.expressed_at)}
                    </span>
                </div>

                {/* Rejection reason */}
                {isRejected && interest.rejection_reason && (
                    <div className="bg-red-50 border-round px-2 py-1 mb-1">
                        <div className="flex align-items-center gap-1 text-red-600" style={{ fontSize: '0.6rem' }}>
                            <i className="pi pi-info-circle" style={{ fontSize: '0.55rem' }} />
                            <span className="text-overflow-ellipsis overflow-hidden white-space-nowrap">
                                {interest.rejection_reason}
                            </span>
                        </div>
                    </div>
                )}

                {/* Countered - waiting */}
                {isCountered && (
                    <div className="bg-orange-50 border-round px-2 py-1 mb-1">
                        <div className="flex align-items-center gap-1 text-orange-700" style={{ fontSize: '0.6rem' }}>
                            <i className="pi pi-hourglass" style={{ fontSize: '0.55rem', animation: 'pulse 2s infinite' }} />
                            <span>Waiting for agent's response</span>
                        </div>
                    </div>
                )}

                {/* Accepted */}
                {isAccepted && (
                    <div className="bg-green-50 border-round px-2 py-1 mb-1">
                        <div className="flex align-items-center gap-1 text-green-700" style={{ fontSize: '0.6rem' }}>
                            <i className="pi pi-check-circle" style={{ fontSize: '0.55rem' }} />
                            <span>Confirmed at {parseFloat(Number(interest.final_percentage).toFixed(2))}%</span>
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-1 pt-1 border-top-1 surface-border">
                    {isPending && (
                        <Button
                            label="Review"
                            icon="pi pi-check"
                            severity="info"
                            className="flex-1"
                            style={{ fontSize: '0.65rem', padding: '0.25rem 0.4rem' }}
                            onClick={() => handleReviewInterest(interest)}
                        />
                    )}
                    {isCountered && (
                        <Button
                            label="Awaiting"
                            icon="pi pi-hourglass"
                            className="p-button-outlined flex-1"
                            severity="warning"
                            style={{ fontSize: '0.65rem', padding: '0.25rem 0.4rem' }}
                            onClick={() => handleReviewInterest(interest)}
                        />
                    )}
                    {isAccepted && (
                        <Button
                            label="Details"
                            icon="pi pi-check-circle"
                            className="p-button-outlined flex-1"
                            severity="success"
                            style={{ fontSize: '0.65rem', padding: '0.25rem 0.4rem' }}
                            onClick={() => handleReviewInterest(interest)}
                        />
                    )}
                    {isRejected && (
                        <Button
                            label="Details"
                            icon="pi pi-info-circle"
                            className="p-button-outlined flex-1"
                            severity="secondary"
                            style={{ fontSize: '0.65rem', padding: '0.25rem 0.4rem' }}
                            onClick={() => handleReviewInterest(interest)}
                        />
                    )}
                    <Button
                        icon="pi pi-eye"
                        label="View"
                        className="p-button-outlined p-button-secondary"
                        style={{ fontSize: '0.65rem', padding: '0.25rem 0.4rem' }}
                        onClick={() => handleViewLead(interest.marketplace_lead_id)}
                    />
                </div>
            </div>
        );
    };

    // ─── List Row Component (flat full-width row — same style as old itemTemplate) ───
    const InterestRow = ({ interest }) => {
        const statusConfig = getInterestStatusConfig(interest.status);
        const isPending = interest.status === 'pending';
        const isCountered = interest.status === 'countered';
        const isAccepted = interest.status === 'accepted';
        const isRejected = interest.status === 'rejected';
        const productConfig = getLoanProductConfig(getProductType(interest.lead));

        return (
            <div className={`surface-card border-round shadow-1 mb-2 overflow-hidden border-left-3 ${getBorderClass(interest.status)}`}>
                {/* Main Content */}
                <div className="p-2">
                    <div className="flex flex-column md:flex-row gap-2">
                        {/* Left: Agent Info + Lead Details */}
                        <div className="flex align-items-start gap-2 flex-1">
                            <Avatar
                                image={interest.interested_agent?.avatar}
                                label={interest.interested_agent?.name?.charAt(0) || 'A'}
                                size="normal"
                                shape="circle"
                                className={`flex-shrink-0 ${getAvatarBg(interest.status)} text-white`}
                                style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.7rem' }}
                            />
                            <div className="flex-1 min-w-0">
                                {/* Agent Name + Status */}
                                <div className="flex align-items-center gap-1 mb-1 flex-wrap">
                                    <span className="font-semibold text-900" style={{ fontSize: '0.75rem' }}>
                                        {interest.interested_agent?.name || 'Agent'}
                                    </span>
                                    <Tag
                                        value={statusConfig.label}
                                        style={{ ...outlineTagStyle(statusConfig.severity), fontSize: '0.55rem', padding: '0.1rem 0.25rem' }}
                                    />
                                </div>

                                {/* Lead Info Row */}
                                <div className="flex align-items-center gap-2 text-500 flex-wrap" style={{ fontSize: '0.65rem' }}>
                                    <span>
                                        <i className="pi pi-file mr-1" style={{ fontSize: '0.55rem' }} />
                                        {getLeadName(interest)}
                                    </span>
                                    <Tag
                                        value={productConfig.label}
                                        style={{ fontSize: '0.5rem', padding: '0 0.25rem', backgroundColor: 'var(--primary-100)', color: 'var(--primary-700)', border: 'none' }}
                                    />
                                    <span className="text-green-600 font-bold">
                                        {formatLoanAmount(interest.lead?.loan_amount)}
                                    </span>
                                    {(interest.lead?.city || interest.lead?.state) && (
                                        <span className="flex align-items-center gap-1 hidden md:inline-flex">
                                            <i className="pi pi-map-marker" style={{ fontSize: '0.55rem' }} />
                                            {[interest.lead?.city, interest.lead?.state].filter(Boolean).join(', ')}
                                        </span>
                                    )}
                                </div>

                                {/* Interest Message */}
                                {interest.interest_message && (
                                    <div className="text-500 mt-1 font-italic white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.6rem' }}>
                                        "{interest.interest_message}"
                                    </div>
                                )}

                                {/* Meta info on desktop */}
                                <div className="hidden md:flex align-items-center gap-2 text-400 mt-1" style={{ fontSize: '0.6rem' }}>
                                    <i className="pi pi-clock" style={{ fontSize: '0.55rem' }} />
                                    <span>{formatTimeAgo(interest.expressed_at)}</span>
                                    {interest.negotiation_round > 1 && (
                                        <>
                                            <span>·</span>
                                            <span>Round {interest.negotiation_round}</span>
                                        </>
                                    )}
                                    {interest.lead?.tracking_code && (
                                        <>
                                            <span>·</span>
                                            <span className="font-mono text-primary">{interest.lead.tracking_code}</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Center: Percentages */}
                        <div className="flex md:flex-column align-items-center justify-content-center gap-1 md:px-2 md:border-left-1 md:border-right-1 surface-border">
                            <div className="flex flex-column align-items-center">
                                <div className="text-500" style={{ fontSize: '0.55rem' }}>Their Offer</div>
                                <div className="bg-blue-50 border-round px-2 py-1">
                                    <span className="text-blue-600 font-bold" style={{ fontSize: '0.75rem' }}>
                                        {parseFloat(Number(interest.proposed_percentage).toFixed(2))}%
                                    </span>
                                </div>
                            </div>
                            {isCountered && interest.counter_percentage && (
                                <div className="flex flex-column align-items-center">
                                    <div className="text-500" style={{ fontSize: '0.55rem' }}>Your Counter</div>
                                    <div className="bg-orange-50 border-round px-2 py-1">
                                        <span className="text-orange-600 font-bold" style={{ fontSize: '0.75rem' }}>
                                            {parseFloat(Number(interest.counter_percentage).toFixed(2))}%
                                        </span>
                                    </div>
                                </div>
                            )}
                            {isAccepted && interest.final_percentage && (
                                <div className="flex flex-column align-items-center">
                                    <div className="text-500" style={{ fontSize: '0.55rem' }}>Agreed</div>
                                    <div className="bg-green-50 border-round px-2 py-1">
                                        <span className="text-green-600 font-bold" style={{ fontSize: '0.75rem' }}>
                                            {parseFloat(Number(interest.final_percentage).toFixed(2))}%
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Right: Actions */}
                        <div className="flex md:flex-column align-items-center md:align-items-end justify-content-between gap-1">
                            {/* Meta info on mobile */}
                            <div className="flex md:hidden align-items-center gap-2 text-400" style={{ fontSize: '0.6rem' }}>
                                <i className="pi pi-clock" style={{ fontSize: '0.55rem' }} />
                                <span>{formatTimeAgo(interest.expressed_at)}</span>
                            </div>

                            <div className="flex align-items-center gap-1">
                                {isPending && (
                                    <Button
                                        label="Review"
                                        icon="pi pi-check"
                                        severity="info"
                                        style={{ fontSize: '0.65rem', padding: '0.25rem 0.4rem' }}
                                        onClick={() => handleReviewInterest(interest)}
                                    />
                                )}
                                {isCountered && (
                                    <Button
                                        label="Awaiting"
                                        icon="pi pi-hourglass"
                                        className="p-button-outlined"
                                        severity="warning"
                                        style={{ fontSize: '0.65rem', padding: '0.25rem 0.4rem' }}
                                        onClick={() => handleReviewInterest(interest)}
                                    />
                                )}
                                {isAccepted && (
                                    <Button
                                        label="Details"
                                        icon="pi pi-check-circle"
                                        className="p-button-outlined"
                                        severity="success"
                                        style={{ fontSize: '0.65rem', padding: '0.25rem 0.4rem' }}
                                        onClick={() => handleReviewInterest(interest)}
                                    />
                                )}
                                {isRejected && (
                                    <Button
                                        label="Details"
                                        icon="pi pi-info-circle"
                                        className="p-button-outlined"
                                        severity="secondary"
                                        style={{ fontSize: '0.65rem', padding: '0.25rem 0.4rem' }}
                                        onClick={() => handleReviewInterest(interest)}
                                    />
                                )}
                                <Button
                                    icon="pi pi-eye"
                                    className="p-button-outlined"
                                    style={{ padding: '0.25rem 0.4rem', fontSize: '0.65rem' }}
                                    tooltip="View Lead"
                                    tooltipOptions={{ position: 'top' }}
                                    onClick={() => handleViewLead(interest.marketplace_lead_id)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Rejection reason footer */}
                {isRejected && interest.rejection_reason && (
                    <div className="bg-red-50 px-2 py-1 border-top-1 surface-border">
                        <div className="flex align-items-center gap-1 text-red-600" style={{ fontSize: '0.6rem' }}>
                            <i className="pi pi-info-circle" style={{ fontSize: '0.55rem' }} />
                            <span>{interest.rejection_reason}</span>
                        </div>
                    </div>
                )}

                {/* Countered - Awaiting response */}
                {isCountered && (
                    <div className="bg-orange-50 px-2 py-1 border-top-1 surface-border">
                        <div className="flex align-items-center gap-1 text-orange-700" style={{ fontSize: '0.6rem' }}>
                            <i className="pi pi-hourglass" style={{ fontSize: '0.55rem', animation: 'pulse 2s infinite' }} />
                            <span>Waiting for agent's response</span>
                        </div>
                    </div>
                )}

                {/* Accepted - Success */}
                {isAccepted && (
                    <div className="bg-green-50 px-2 py-1 border-top-1 surface-border">
                        <div className="flex align-items-center gap-1 text-green-700" style={{ fontSize: '0.6rem' }}>
                            <i className="pi pi-check-circle" style={{ fontSize: '0.55rem' }} />
                            <span>Confirmed at {parseFloat(Number(interest.final_percentage).toFixed(2))}%</span>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // ─── Loading Skeleton ───
    const loadingSkeleton = () => {
        if (viewMode === 'list') {
            return (
                <div className="flex flex-column gap-2">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="surface-card border-round shadow-1 p-2" style={{ borderLeft: '3px solid var(--surface-300)' }}>
                            <div className="flex flex-column md:flex-row gap-2">
                                <div className="flex align-items-center gap-2 flex-1">
                                    <Skeleton shape="circle" size="1.75rem" />
                                    <div className="flex-1">
                                        <Skeleton width="50%" height="0.75rem" className="mb-1" />
                                        <Skeleton width="35%" height="0.65rem" />
                                    </div>
                                </div>
                                <div className="flex align-items-center gap-2">
                                    <Skeleton width="50px" height="1.5rem" />
                                    <Skeleton width="50px" height="1.5rem" />
                                </div>
                                <div className="flex align-items-center gap-1">
                                    <Skeleton width="55px" height="1.4rem" />
                                    <Skeleton width="28px" height="1.4rem" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            );
        }
        return (
            <div className="grid">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="col-12 md:col-6 lg:col-4 xl:col-3 p-2">
                        <div className="surface-card border-round border-1 surface-border p-2" style={{ borderLeft: '3px solid var(--surface-300)' }}>
                            <div className="flex align-items-center justify-content-between mb-2">
                                <Skeleton width="70px" height="0.9rem" />
                                <Skeleton width="22px" height="22px" borderRadius="4px" />
                            </div>
                            <Skeleton width="65%" height="0.8rem" className="mb-1" />
                            <Skeleton width="40%" height="0.85rem" className="mb-1" />
                            <Skeleton width="50%" height="0.65rem" className="mb-2" />
                            <div className="border-top-1 surface-border pt-1 mt-1">
                                <div className="flex align-items-center gap-1 mb-2">
                                    <Skeleton shape="circle" size="1.4rem" />
                                    <Skeleton width="60%" height="0.7rem" />
                                </div>
                                <Skeleton width="100%" height="1.2rem" className="mb-1" />
                                <Skeleton width="80%" height="1.2rem" />
                            </div>
                            <div className="flex justify-content-between mt-2">
                                <Skeleton width="45px" height="0.65rem" />
                                <Skeleton width="55px" height="0.65rem" />
                            </div>
                            <div className="border-top-1 surface-border pt-1 mt-2 flex gap-1">
                                <Skeleton width="60%" height="1.4rem" />
                                <Skeleton width="38%" height="1.4rem" />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // Get empty state content based on filter
    const getEmptyStateContent = () => {
        if (debouncedSearchQuery) {
            return {
                icon: 'pi-search',
                title: 'No results found',
                message: `No interests match "${debouncedSearchQuery}"`,
                bgColor: 'var(--surface-100)',
                iconColor: 'var(--text-color-secondary)',
                isPositive: false
            };
        }

        switch (statusFilter) {
            case 'pending':
                return {
                    icon: 'pi-check',
                    title: 'All caught up!',
                    message: 'No interests waiting for your review',
                    bgColor: 'var(--blue-50)',
                    iconColor: 'var(--blue-500)',
                    isPositive: true
                };
            case 'countered':
                return {
                    icon: 'pi-clock',
                    title: 'No counter offers sent',
                    message: 'Counter offers you send will appear here',
                    bgColor: 'var(--orange-50)',
                    iconColor: 'var(--orange-500)',
                    isPositive: false
                };
            case 'accepted':
                return {
                    icon: 'pi-star',
                    title: 'No accepted interests yet',
                    message: 'Accepted collaborations will appear here',
                    bgColor: 'var(--green-50)',
                    iconColor: 'var(--green-500)',
                    isPositive: false
                };
            case 'rejected':
                return {
                    icon: 'pi-thumbs-up',
                    title: 'No rejections',
                    message: "You haven't rejected any interests",
                    bgColor: 'var(--green-50)',
                    iconColor: 'var(--green-500)',
                    isPositive: true
                };
            default:
                return {
                    icon: 'pi-inbox',
                    title: 'No interests received',
                    message: 'Post leads to start receiving interest from other agents!',
                    bgColor: 'var(--primary-50)',
                    iconColor: 'var(--primary-500)',
                    isPositive: false
                };
        }
    };

    // Empty template
    const emptyTemplate = () => {
        const { icon, title, message, bgColor, iconColor, isPositive } = getEmptyStateContent();

        return (
            <div className="surface-card border-round shadow-1 p-2">
                <div className="flex flex-column align-items-center justify-content-center py-4">
                    <div
                        className="border-circle p-3 mb-2 flex align-items-center justify-content-center"
                        style={{ backgroundColor: bgColor, width: '3rem', height: '3rem' }}
                    >
                        <i className={`pi ${icon}`} style={{ color: iconColor, fontSize: '1.25rem' }} />
                    </div>
                    <h3 className="text-900 font-semibold mb-1 m-0" style={{ fontSize: '0.85rem' }}>{title}</h3>
                    <p className="text-500 mb-2 text-center px-3 line-height-3 m-0" style={{ maxWidth: '260px', fontSize: '0.7rem' }}>
                        {message}
                    </p>
                    {!isPositive && (
                        <Button
                            label="My Listings"
                            icon="pi pi-list"
                            className="p-button-outlined"
                            style={{ fontSize: '0.7rem', padding: '0.3rem 0.5rem' }}
                            onClick={() => navigate('/app/marketplace/my-listings')}
                        />
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="received-interests">
            <NavigationTabMenu tabs={SELL_LEADS_TABS} />
            <Toast ref={toast} />
            <Tooltip target=".custom-tooltip" />

            {/* Compact Header */}
            <div className="surface-card border-round shadow-1 p-2 mb-2">
                <div className="flex align-items-center justify-content-between">
                    <div className="flex align-items-center gap-1">
                        <Button
                            icon="pi pi-arrow-left"
                            className="p-button-text p-button-rounded"
                            onClick={() => navigate('/app/marketplace')}
                            tooltip="Back to Marketplace"
                            tooltipOptions={{ position: 'top' }}
                            style={{ width: '1.75rem', height: '1.75rem' }}
                        />
                        <h2 className="text-900 font-bold m-0" style={{ fontSize: '1rem' }}>Buyer Enquiries</h2>
                        {pendingCount > 0 && (
                            <Badge value={pendingCount} severity="warning" style={{ fontSize: '0.6rem' }} />
                        )}
                    </div>
                    <div className="flex align-items-center gap-1">
                        <Button
                            label="My Listings"
                            icon="pi pi-list"
                            className="hidden md:inline-flex"
                            style={{ fontSize: '0.7rem', padding: '0.35rem 0.6rem' }}
                            onClick={() => navigate('/app/marketplace/my-listings')}
                        />
                        <Button
                            icon="pi pi-list"
                            label="Listings"
                            className="md:hidden"
                            style={{ fontSize: '0.65rem', padding: '0.3rem 0.5rem' }}
                            onClick={() => navigate('/app/marketplace/my-listings')}
                        />
                    </div>
                </div>
            </div>

            {/* Action Required - Text only */}
            {pendingCount > 0 && !statusFilter && (
                <div className="flex align-items-center gap-2 mb-2 px-2 text-orange-600" style={{ fontSize: '0.75rem' }}>
                    <i className="pi pi-bell" style={{ fontSize: '0.7rem' }} />
                    <span className="font-medium">
                        {pendingCount} interest{pendingCount > 1 ? 's' : ''} need your review
                    </span>
                </div>
            )}

            {/* Filters Row - Grid layout for proper alignment */}
            <div className="surface-card border-round shadow-1 p-2 mb-2">
                <div className="grid align-items-end m-0">
                    {/* Search */}
                    <div className="col-6 md:col-3 lg:col-3 p-1">
                        <label className="block text-500 mb-1" style={{ fontSize: '0.65rem' }}>Search</label>
                        <span className="p-input-icon-left w-full">
                            <i className="pi pi-search" style={{ fontSize: '0.7rem' }} />
                            <InputText
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Agent, tracking code..."
                                className="w-full p-inputtext-sm"
                                style={{ fontSize: '0.7rem', padding: '0.5rem 0.5rem 0.5rem 2rem' }}
                            />
                        </span>
                    </div>

                    {/* Loan Type */}
                    <div className="col-6 md:col-3 lg:col-3 p-1">
                        <label className="block text-500 mb-1" style={{ fontSize: '0.65rem' }}>Loan Type</label>
                        <Dropdown
                            value={loanTypeFilter}
                            options={LOAN_TYPE_OPTIONS}
                            onChange={(e) => setLoanTypeFilter(e.value)}
                            placeholder="All Products"
                            optionLabel="label"
                            optionValue="value"
                            showClear
                            filter
                            filterPlaceholder="Search..."
                            className="w-full p-inputtext-sm"
                            panelStyle={{ fontSize: '0.7rem' }}
                            pt={{ input: { style: { fontSize: '0.7rem' } }, item: { style: { fontSize: '0.7rem', padding: '0.4rem 0.75rem' } }, filterInput: { style: { fontSize: '0.7rem' } } }}
                        />
                    </div>

                    {/* From Date */}
                    <div className="col-6 md:col-2 lg:col-2 p-1">
                        <label className="block text-500 mb-1" style={{ fontSize: '0.65rem' }}>From Date</label>
                        <Calendar
                            value={startDate}
                            onChange={(e) => setStartDate(e.value)}
                            placeholder="From"
                            className="w-full p-inputtext-sm"
                            showIcon
                            inputStyle={{ fontSize: '0.7rem' }}
                            dateFormat="dd M yy"
                            showButtonBar
                            maxDate={endDate || undefined}
                            pt={{
                                input: { style: { fontSize: '0.7rem' } },
                                dropdownButton: { style: { width: '2rem', padding: '0' } },
                                icon: { style: { fontSize: '0.75rem' } }
                            }}
                        />
                    </div>

                    {/* To Date */}
                    <div className="col-6 md:col-2 lg:col-2 p-1">
                        <label className="block text-500 mb-1" style={{ fontSize: '0.65rem' }}>To Date</label>
                        <Calendar
                            value={endDate}
                            onChange={(e) => setEndDate(e.value)}
                            placeholder="To"
                            className="w-full p-inputtext-sm"
                            showIcon
                            inputStyle={{ fontSize: '0.7rem' }}
                            dateFormat="dd M yy"
                            showButtonBar
                            minDate={startDate || undefined}
                            pt={{
                                input: { style: { fontSize: '0.7rem' } },
                                dropdownButton: { style: { width: '2rem', padding: '0' } },
                                icon: { style: { fontSize: '0.75rem' } }
                            }}
                        />
                    </div>

                    {/* View Toggle + Refresh */}
                    <div className="col-6 md:col-2 lg:col-2 p-1 flex align-items-end justify-content-end gap-1">
                        <div className="hidden md:flex align-items-center">
                            <SelectButton
                                value={viewMode}
                                options={viewOptions}
                                onChange={(e) => e.value && setViewMode(e.value)}
                                optionLabel="icon"
                                itemTemplate={(option) => <i className={option.icon} style={{ fontSize: '0.7rem' }} />}
                                style={{ transform: 'scale(0.85)' }}
                            />
                        </div>
                        <Button
                            icon="pi pi-refresh"
                            className="p-button-text p-button-rounded"
                            onClick={loadInterests}
                            loading={loading}
                            tooltip="Refresh"
                            tooltipOptions={{ position: 'top' }}
                            style={{ width: '1.75rem', height: '1.75rem' }}
                        />
                    </div>
                </div>

                {/* Active filters & Results count */}
                {(loanTypeFilter || startDate || endDate) && (
                    <div className="flex align-items-center justify-content-between mt-1 pt-1 border-top-1 surface-border">
                        <span className="text-500" style={{ fontSize: '0.7rem' }}>
                            {[loanTypeFilter, startDate, endDate].filter(Boolean).length} filter{[loanTypeFilter, startDate, endDate].filter(Boolean).length > 1 ? 's' : ''} active
                        </span>
                        <Button
                            label="Clear"
                            icon="pi pi-filter-slash"
                            className="p-button-text p-button-sm"
                            onClick={() => { setLoanTypeFilter(null); setStartDate(null); setEndDate(null); setSearchQuery(''); setDebouncedSearchQuery(''); }}
                            style={{ fontSize: '0.65rem', padding: '0.2rem 0.4rem' }}
                        />
                    </div>
                )}
            </div>

            {/* Status Filter Chips */}
            {!loading && (
                <div className="surface-card border-round shadow-1 p-2 mb-2">
                    <div className="flex flex-wrap md:flex-nowrap gap-1">
                        {[
                            { key: '', label: 'All', icon: 'pi-list', color: 'primary', badge: totalRecords },
                            { key: 'pending', label: 'Pending', icon: 'pi-clock', color: 'blue', badge: summary.pending },
                            { key: 'countered', label: 'Counter', icon: 'pi-replay', color: 'orange', badge: summary.countered },
                            { key: 'accepted', label: 'Accepted', icon: 'pi-check-circle', color: 'green', badge: summary.accepted },
                            { key: 'rejected', label: 'Rejected', icon: 'pi-times-circle', color: 'red', badge: summary.rejected }
                        ].map(chip => (
                            <div
                                key={chip.key}
                                className="flex align-items-center justify-content-center gap-1 px-2 py-1 border-round cursor-pointer transition-all transition-duration-200"
                                style={{
                                    backgroundColor: statusFilter === chip.key ? `var(--${chip.color}-100)` : 'var(--surface-100)',
                                    border: statusFilter === chip.key ? `1px solid var(--${chip.color}-300)` : '1px solid var(--surface-200)',
                                    flex: '1 1 18%',
                                    fontSize: '0.7rem'
                                }}
                                onClick={() => { setStatusFilter(chip.key); setPage(1); setFirst(0); }}
                            >
                                <i className={`pi ${chip.icon} text-${chip.color}-500`} style={{ fontSize: '0.65rem' }} />
                                <span className="text-700 font-medium">{chip.label}</span>
                                <Badge value={chip.badge ?? 0} severity={chip.key === '' ? 'primary' : chip.key === 'pending' ? 'info' : chip.key === 'accepted' ? 'success' : chip.key === 'rejected' ? 'danger' : 'warning'} style={{ fontSize: '0.55rem', minWidth: '1.1rem', height: '1.1rem', lineHeight: '1.1rem' }} />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Interests Content */}
            {loading ? (
                loadingSkeleton()
            ) : interests.length === 0 ? (
                emptyTemplate()
            ) : (
                <>
                    {viewMode === 'list' ? (
                        <div className="flex flex-column">
                            {interests.map(interest => (
                                <InterestRow key={interest.id} interest={interest} />
                            ))}
                        </div>
                    ) : (
                        <div className="grid">
                            {interests.map(interest => (
                                <div key={interest.id} className="col-12 md:col-6 lg:col-4 xl:col-3 p-2">
                                    <InterestCard interest={interest} />
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Paginator */}
                    <div style={{ visibility: totalRecords > rowsPerPage ? 'visible' : 'hidden' }}>
                        <Paginator
                            first={first}
                            rows={rowsPerPage}
                            totalRecords={totalRecords || 0}
                            onPageChange={onPageChange}
                            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                            className="surface-card border-round shadow-1 mt-2"
                            pt={{
                                root: { style: { padding: '0.25rem', fontSize: '0.65rem' } },
                                firstPageButton: { style: { minWidth: '1.5rem', height: '1.5rem' } },
                                previousPageButton: { style: { minWidth: '1.5rem', height: '1.5rem' } },
                                nextPageButton: { style: { minWidth: '1.5rem', height: '1.5rem' } },
                                lastPageButton: { style: { minWidth: '1.5rem', height: '1.5rem' } },
                                pageButton: { style: { minWidth: '1.5rem', height: '1.5rem', fontSize: '0.65rem' } }
                            }}
                        />
                    </div>
                </>
            )}

            {/* Review Interest Dialog */}
            <ReviewInterestDialog
                visible={showReviewDialog}
                onHide={() => {
                    setShowReviewDialog(false);
                    setSelectedInterest(null);
                }}
                interest={selectedInterest}
                onSuccess={() => {
                    loadInterests();
                }}
            />

            {/* CSS overrides */}
            <style>{`
                @keyframes pulse {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.1); }
                }
                .received-interests .p-calendar .p-inputtext {
                    padding: 0.5rem 0.5rem;
                    font-size: 0.7rem;
                }
                .received-interests .p-calendar .p-datepicker-trigger {
                    width: 2rem;
                    padding: 0;
                }
                .received-interests .p-calendar .p-datepicker-trigger .p-icon {
                    width: 0.75rem;
                    height: 0.75rem;
                }
                .received-interests .p-dropdown .p-dropdown-label {
                    padding: 0.5rem 0.5rem;
                    font-size: 0.7rem;
                }
                .received-interests .p-dropdown .p-dropdown-trigger {
                    width: 2rem;
                }
                @media screen and (max-width: 768px) {
                    .received-interests .p-calendar,
                    .received-interests .p-dropdown {
                        font-size: 0.65rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default ReceivedInterests;
